'use client'

import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Text } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { PriceRange, Filter } from '@marketplace-web/catalog/catalog-data'
import { CurrencyAmountModel, formatCurrency } from '@marketplace-web/currency/currency-data'
import {
  useCatalogConfigurationContext,
  useCatalogContext,
  clickFilterEvent,
} from '@marketplace-web/catalog/catalog-page-data'
import { Screen } from 'constants/tracking/screens'

import useFetchDynamicFilters from '../../../../hooks/useFetchDynamicFilters'
import FilterDropdown from '../../common/FilterDropdown'
import PriceRangeInput from '../../common/PriceRangeInput'

type Props = {
  minimumValidation?: CurrencyAmountModel
}

const PriceFilter = ({ minimumValidation }: Props) => {
  const { track } = useTracking()
  const fetchDynamicFilters = useFetchDynamicFilters()
  const {
    state: { filters, dynamicFilters, searchSession },
  } = useCatalogContext()
  const serverConfiguration = useCatalogConfigurationContext()

  const configuration = serverConfiguration
  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchSession
  const searchSessionId = searchSession.sessionId
  const { locale } = useIntl()
  const { priceFrom, priceTo } = filters
  const { selectedDynamicFilters } = dynamicFilters

  const defaultCurrency = configuration.config.currency
  const filterCurrency = filters.currency
  const currency = filterCurrency || defaultCurrency

  const [shouldUpdatePrice, setShouldUpdatePrice] = useState(false)
  const [isPriceRangeUpToDate, setIsPriceRangeUpToDate] = useState(true)

  const range: PriceRange = {
    priceFrom,
    priceTo,
  }

  const handleOnFilterClick = () => {
    track(
      clickFilterEvent({
        filter: Filter.Price,
        screen: Screen.CatalogFilters,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      }),
    )
  }

  const handlePriceRangeChange = (priceRange: PriceRange) => {
    fetchDynamicFilters({
      selectedDynamicFilters,
      selectedFilters: { currency, priceFrom: priceRange.priceFrom, priceTo: priceRange.priceTo },
    })
  }

  const handleDropdownOpen = () => {
    setShouldUpdatePrice(true)
    setIsPriceRangeUpToDate(true)
  }

  const handleDropdownClose = () => {
    setShouldUpdatePrice(false)
    setIsPriceRangeUpToDate(false)
  }

  useEffect(() => {
    if (!minimumValidation?.amount) return

    const newPriceFrom =
      priceFrom && parseFloat(priceFrom) < parseFloat(minimumValidation.amount) ? null : priceFrom

    const newPriceTo =
      priceTo && parseFloat(priceTo) < parseFloat(minimumValidation.amount) ? null : priceTo

    const priceRange = {
      priceFrom: newPriceFrom,
      priceTo: newPriceTo,
    }

    handlePriceRangeChange(priceRange)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimumValidation?.amount])

  const renderDropdown = () => (
    <div className="u-ui-padding-vertical-x-small">
      <div className="u-flexbox">
        <PriceRangeInput
          priceRange={range}
          priceFromTitle={<FormattedMessage id="catalog.filters.price.price_from" />}
          priceToTitle={<FormattedMessage id="catalog.filters.price.price_to" />}
          onPriceRangeChange={handlePriceRangeChange}
          shouldUpdateInputValues={shouldUpdatePrice}
          shouldFocusInput={isPriceRangeUpToDate}
          minimumValidation={minimumValidation}
        />
      </div>

      {minimumValidation && (
        <div className="u-ui-padding-horizontal-medium u-ui-padding-bottom-regular">
          <Text
            type={Text.Type.Caption}
            theme="muted"
            as="span"
            text={
              <FormattedMessage
                id="catalog.filters.price.designer_price_message"
                values={{ value: formatCurrency(minimumValidation.amount, { locale, currency }) }}
              />
            }
          />
        </div>
      )}
    </div>
  )

  return (
    <div className="u-ui-margin-right-regular u-ui-margin-bottom-regular">
      <FilterDropdown
        title={<FormattedMessage id="catalog.filters.price.name" />}
        areAnyOfItemsSelected={!!(priceTo || priceFrom)}
        onOpen={handleDropdownOpen}
        onClose={handleDropdownClose}
        onFilterClick={handleOnFilterClick}
        testId="catalog--price-filter"
        useChipAsTrigger
      >
        {renderDropdown()}
      </FilterDropdown>
    </div>
  )
}

export default PriceFilter
